import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2, X, Sparkles } from "lucide-react";

const CATEGORY_OPTIONS = [
  "Wooden & Laminate Flooring",
  "Designer Wallpapers",
  "Luxury Carpets & Mats",
  "Artificial Turf / Doormats",
  "Mixed Project Requirement",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const initialState = {
  name: "",
  mobile: "",
  product_category: CATEGORY_OPTIONS[0],
  quantity: "",
  message: "",
};

export default function Inquiry() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your name.";
    if (!/^[0-9+\-\s()]{7,}$/.test(form.mobile.trim())) return "Please enter a valid mobile number.";
    if (!form.product_category) return "Please select a product category.";
    if (form.quantity.trim().length < 1) return "Please enter an estimated quantity.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSuccessOpen(true);
      setForm(initialState);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please call us at 098450 48406.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry" className="relative py-24 lg:py-32 bg-slate-950 overflow-hidden" data-testid="inquiry-section">
      <div className="absolute inset-0">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-amber-700/15 blur-3xl" />
        <div className="absolute inset-0 bg-grain opacity-30" />
      </div>

      <div className="relative mx-auto max-w-5xl px-5 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <p className="eyebrow !text-amber-500 mb-4">Bulk Inquiry · Quote Engine</p>
            <h2 className="font-display text-white text-4xl md:text-5xl font-medium leading-[1.05] tracking-tight">
              Get a Customized <span className="italic text-amber-500">Wholesale Quote Sheet.</span>
            </h2>
            <p className="mt-5 text-white/65 leading-relaxed">
              Share your project requirements and our distribution team will respond with a tailored price list, sample dispatch and an architect-friendly spec sheet — typically within an hour during business hours.
            </p>
            <ul className="mt-7 space-y-3 text-white/70 text-sm">
              {[
                "Direct container-load pricing",
                "Samples couriered free for serious enquiries",
                "GST invoice & project-grade documentation",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <Sparkles className="h-4 w-4 text-amber-500" /> {t}
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-white border border-white/10 p-7 lg:p-10"
            data-testid="inquiry-form"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name" name="name" value={form.name} onChange={onChange} placeholder="e.g. Arjun Mehta" testid="inquiry-input-name" />
              <Field label="Mobile Number" name="mobile" value={form.mobile} onChange={onChange} placeholder="+91 98450 48406" testid="inquiry-input-mobile" />
              <div className="md:col-span-1">
                <Label>Product Category</Label>
                <select
                  name="product_category"
                  value={form.product_category}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-slate-300 focus:border-amber-700 outline-none py-3 text-slate-900 text-sm appearance-none"
                  data-testid="inquiry-select-category"
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <Field label="Estimated Quantity (Sq Ft / Units)" name="quantity" value={form.quantity} onChange={onChange} placeholder="e.g. 2500 sq ft" testid="inquiry-input-quantity" />
              <div className="md:col-span-2">
                <Label>Project Details / Message</Label>
                <textarea
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us about the site, timelines and any specific brand or finish you prefer…"
                  className="w-full bg-transparent border-b border-slate-300 focus:border-amber-700 outline-none py-3 text-slate-900 text-sm resize-none"
                  data-testid="inquiry-input-message"
                />
              </div>
            </div>

            {error && (
              <p className="mt-5 text-sm text-red-600" data-testid="inquiry-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-8 w-full sm:w-auto glow-amber bg-amber-700 hover:bg-amber-800 disabled:opacity-70 text-white px-8 py-4 text-[12px] tracking-[0.24em] uppercase inline-flex items-center justify-center gap-3 transition-colors"
              data-testid="inquiry-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  Submit Wholesale Request <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <p className="mt-5 text-xs text-slate-500 leading-relaxed">
              By submitting, you agree to be contacted by Shri Gurunath Enterprises about your project enquiry. We never spam.
            </p>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal open={successOpen} onClose={() => setSuccessOpen(false)} />
    </section>
  );
}

function Label({ children }) {
  return <span className="block eyebrow !text-slate-500 mb-2">{children}</span>;
}

function Field({ label, name, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-slate-300 focus:border-amber-700 outline-none py-3 text-slate-900 text-sm placeholder:text-slate-400"
        data-testid={testid}
      />
    </div>
  );
}

function SuccessModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-5"
      role="dialog"
      aria-modal="true"
      data-testid="inquiry-success-modal"
    >
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white border border-slate-200 p-8 md:p-10 animate-fade-up">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 inline-flex items-center justify-center text-slate-500 hover:text-amber-700"
          aria-label="Close"
          data-testid="inquiry-modal-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-3 mb-5">
          <span className="inline-flex h-12 w-12 items-center justify-center bg-amber-700/10 border border-amber-700/40 text-amber-700">
            <CheckCircle2 className="h-5 w-5" />
          </span>
          <p className="eyebrow">Request Received</p>
        </div>
        <h3 className="font-display text-3xl md:text-4xl text-slate-900 leading-tight">Thank you!</h3>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Our wholesale distribution team will review your project requirements and message you a tailored price list within <span className="font-medium text-amber-700">1 hour</span>.
        </p>
        <div className="mt-7 pt-6 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <p className="text-sm text-slate-500">Need urgent help?</p>
          <a
            href="https://wa.me/919845048406"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 text-[12px] tracking-[0.22em] uppercase"
            data-testid="inquiry-modal-whatsapp"
          >
            Chat on WhatsApp
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
